import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import api from '../api';
import TaskForm from './TaskForm';
import TaskNotifySheet from './TaskNotifySheet';
import AttachmentViewer from './AttachmentViewer';
import { Calendar, AlertTriangle, Clock, CalendarPlus, X, ArrowLeft, ChevronDown, ChevronUp, Paperclip, UserCheck, Play, Pause, CheckCircle, RotateCcw, LogOut, Check, Pencil, Trash2, Share2, Bell, ListTree, CornerDownRight, Hand, MoreHorizontal } from 'lucide-react';

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('hr-HR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function Avatar({ name, size = 30, color }) {
  const initials = (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const colors = ['#0984e3','#6c5ce7','#00b894','#e17055','#fd79a8','#fdcb6e','#74b9ff','#a29bfe'];
  const bg = color || colors[(name || '').charCodeAt(0) % colors.length];
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: bg, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.38, fontWeight: 600, flexShrink: 0, letterSpacing: '-0.5px' }}>
      {initials}
    </div>
  );
}

function WorkStatusPill({ status }) {
  const config = {
    pending:     { label: 'Nije započeto', bg: '#f1f2f6', color: '#636e72' },
    in_progress: { label: 'U tijeku',      bg: '#e8f4ff', color: '#0984e3' },
    done:        { label: 'Završeno',       bg: '#d4f5ec', color: '#00b894' },
    failed:      { label: 'Neuspješno',    bg: '#fde8e8', color: '#d63031' },
  };
  const c = config[status] || config.pending;
  return <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: c.bg, color: c.color, whiteSpace: 'nowrap' }}>{c.label}</span>;
}

// ─── Nadzorna ploča — prigušeni tonovi + "pečat" stanja ─────────────────────
const TDM_HUE = { pending: '#5b6472', in_progress: '#3a6ea3', done: '#3f8f6b', failed: '#ac463b', overdue: '#ac463b', proposed: '#a8781f' };
const TDM_STAMP = {
  pending:     { Icon: Clock,         word: 'čeka početak' },
  in_progress: { Icon: Play,          word: 'u tijeku' },
  done:        { Icon: CheckCircle,   word: 'završeno' },
  failed:      { Icon: X,             word: 'neuspješno' },
  overdue:     { Icon: AlertTriangle, word: 'kasni' },
  proposed:    { Icon: Clock,         word: 'prijedlog' },
};
function StatusStamp({ kind }) {
  const { Icon, word } = TDM_STAMP[kind] || TDM_STAMP.pending;
  const c = TDM_HUE[kind] || TDM_HUE.pending;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: c, border: `1px solid ${c}`, borderRadius: 6, padding: '2px 8px', whiteSpace: 'nowrap' }}>
      <Icon size={12} /> {word}
    </span>
  );
}

const WS_LABEL = { in_progress: 'U tijeku', done: 'Završeno', failed: 'Neuspješno' };
const wsLabel = (s) => WS_LABEL[s] || 'Nije započeto';

// Helper hook za animirani collapse
function useAnimatedCollapse(initial = false) {
  const [open, setOpen] = useState(initial);
  const [visible, setVisible] = useState(initial);
  const timerRef = useRef(null);

  const toggle = () => {
    if (open) {
      setVisible(false);
      timerRef.current = setTimeout(() => setOpen(false), 220);
    } else {
      setOpen(true);
      setVisible(true);
    }
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return { open, visible, toggle };
}

function TaskDetailModal({ task: initialTask, user, onClose, onUpdate, taskCountMode = 'assigned', startEditing = false, startDetailsOpen = false, startParticipantsOpen = false }) {
  const [task, setTask] = useState(initialTask);
  // Uređivanje polja zadatka, priloga i podzadataka ide kroz TaskForm; sudionici se uređuju u panelu Sudionici.
  const [formOpen, setFormOpen] = useState(!!startEditing);
  const [notifyOpen, setNotifyOpen] = useState(false);          // „Pošalji obavijest” panel
  const [toast, setToast] = useState('');
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = React.useRef(null);
  const moreBtnRef = React.useRef(null);
  const moreMenuPortalRef = React.useRef(null);
  const [moreMenuPos, setMoreMenuPos] = useState({ top: 0, right: 0 });
  const [error, setError] = useState('');
  const [volunteers, setVolunteers] = useState([]);
  const [isVolunteer, setIsVolunteer] = useState(false);
  const [removalRequests, setRemovalRequests] = useState([]);
  const [showRemovalForm, setShowRemovalForm] = useState(false);
  const [removalMessage, setRemovalMessage] = useState('');
  const [myRemovalRequest, setMyRemovalRequest] = useState(null);
  const [remDecide, setRemDecide] = useState(null);            // voditelj: { id, mode: 'ok' | 'no' } — potvrda odjave / odbijanje s razlogom
  const [remReason, setRemReason] = useState('');
  const [remBusy, setRemBusy] = useState(null);                // id zahtjeva (ili 'mine') dok traje odluka / slanje / povlačenje
  const [remSeen, setRemSeen] = useState(() => {               // član: zatvorene napomene o odbijenoj odjavi (id-evi)
    try { return JSON.parse(localStorage.getItem('tdm_rr_seen') || '[]'); } catch { return []; }
  });
  const [confirmRevert, setConfirmRevert] = useState(false);   // inline potvrda za "done" -> "u tijeku"
  const [confirmUnvolunteer, setConfirmUnvolunteer] = useState(false);   // inline potvrda odjave dok se čeka prihvat
  const [declineFor, setDeclineFor] = useState(null);          // email zainteresiranog kojem se upravo odbija prijava
  const [declineReason, setDeclineReason] = useState('');
  const [volBusy, setVolBusy] = useState(null);                // email (ili 'all') dok traje odobravanje/odbijanje
  const [taskGuests, setTaskGuests] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [previewAttachment, setPreviewAttachment] = useState(null);   // prilog otvoren u AttachmentViewer (X ili „natrag” zatvara, ostaje se u aplikaciji)
  const [shareCopied, setShareCopied] = useState(false);
  const [showClosePanel, setShowClosePanel] = useState(false);
  const [closeResult, setCloseResult] = useState('success');
  const [closeReason, setCloseReason] = useState('');
  const [closing, setClosing] = useState(false);
  const [switchingTask, setSwitchingTask] = useState(false);
  // Povijest zadataka kroz koje je korisnik prošao unutar modala (npr. glavni zadatak -> podzadatak).
  // Dok stack nije prazan, X / klik izvan / breadcrumb vraćaju korak nazad umjesto zatvaranja modala.
  const [navStack, setNavStack] = useState([]);
  // Prijedlog: TaskForm ('approve' admin / 'proposal' predlagatelj), odbijanje s razlogom, potvrda povlačenja
  const [propForm, setPropForm] = useState(null);
  const [propReject, setPropReject] = useState(false);
  const [propReason, setPropReason] = useState('');
  const [propWithdraw, setPropWithdraw] = useState(false);
  const [propBusy, setPropBusy] = useState(false);

  // Animirani collapsevi
  const [partView, setPartView] = useState('main');      // panel sudionika: 'main' | 'wait' (čeka se odgovor) | 'out' (nisu na zadatku) — iz ⋯
  const [partMenu, setPartMenu] = useState(false);        // ⋯ izbornik u zaglavlju panela sudionika
  const [approveOpen, setApproveOpen] = useState(false);  // voditelj: blok „Čekaju odobrenje” proširen
  const detailsCollapse = useAnimatedCollapse(!!startDetailsOpen);
  const detailsSectionRef = useRef(null);
  // Redoslijed braće-podzadataka za swipe lijevo = sljedeći podzadatak (postavlja se pri otvaranju podzadatka iz liste)
  const [siblingIds, setSiblingIds] = useState([]);
  // Sheet sudionika — puni ekran, isti obrazac gesta kao FilterSheet (bez otpora/praga-prije-hvatanja)
  const [participantsOpen, setParticipantsOpen] = useState(false);     // montiran (uklj. izlaznu animaciju)
  const [participantsShown, setParticipantsShown] = useState(false);   // vidljiv (enter/exit tranzicija)
  const [participantsDragY, setParticipantsDragY] = useState(0);
  const [participantsDragging, setParticipantsDragging] = useState(false);
  const [partSheetPos, setPartSheetPos] = useState({ bottom: 0, maxH: 0 });   // panel raste prema gore iz trake sudionika
  const [isMobileTDM, setIsMobileTDM] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);
  const partHdrRef = useRef({ y: 0, active: false, cap: false, id: 0, el: null });
  const partListRef = useRef(null);
  const rosterBarRef = useRef(null);
  const tdmModalRef = useRef(null);
  const bodyRef = useRef(null);
  const partTimerRef = useRef(null);
  const PARTICIPANTS_DISMISS = 100; // px pomaka za zatvaranje (isti prag kao FilterSheet)

  // ── Geste na cijelom modalu (samo mobitel, samo pregled) ──
  //   swipe dolje (kad je body na vrhu) → zatvori;  swipe desno (ako ima glavni zadatak) → natrag
  const [navDrag, setNavDrag] = useState({ x: 0, y: 0, active: false });
  const navGRef = useRef({ x0: 0, y0: 0, axis: null, tracking: false });
  const NAV_LOCK = 12;      // px prije zaključavanja osi
  const NAV_CLOSE_DY = 110; // px prema dolje za zatvaranje
  const NAV_BACK_DX = 90;   // px udesno za natrag na glavni

  // Scroll-lock pozadine dok je modal otvoren (isto kao FilterSheet)
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    const onR = () => { setIsMobileTDM(window.innerWidth <= 768); if (participantsOpen) measurePartSheet(); };
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, [participantsOpen]);
  useEffect(() => () => { if (partTimerRef.current) clearTimeout(partTimerRef.current); }, []);

  // Izmjeri gdje panel sudionika smije rasti: od gornjeg ruba trake sudionika prema vrhu modala
  const measurePartSheet = () => {
    const m = tdmModalRef.current, r = rosterBarRef.current;
    if (!m || !r) return;
    const mr = m.getBoundingClientRect(), rr = r.getBoundingClientRect();
    // visina (traka + footer); floor − 1 px da panel uvijek malo zađe ispod trake (traka je iznad, z-index 30)
    // — inače zaokruživanje ostavi pukotinu kroz koju se vidi pozadina
    const bottom = Math.max(0, Math.floor(mr.bottom - rr.top) - 1);
    const availAbove = rr.top - mr.top - 12;                          // slobodno iznad trake, uz 12px daha na vrhu
    const maxH = Math.max(160, Math.min(Math.round(window.innerHeight * 0.58), Math.round(availAbove)));
    setPartSheetPos({ bottom, maxH, maxFull: Math.max(160, Math.round(availAbove)) });
  };

  // Dok je panel otvoren, ponovno izmjeri kad se traka/footer ili modal promijene (npr. promjena
  // kontekstualne akcije ispod, animacija visine modala, skrivanje adresne trake na mobitelu).
  useEffect(() => {
    if (!participantsOpen || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => measurePartSheet());
    if (tdmModalRef.current) ro.observe(tdmModalRef.current);
    if (rosterBarRef.current) ro.observe(rosterBarRef.current);
    const footer = rosterBarRef.current?.nextElementSibling;
    if (footer) ro.observe(footer);
    const t = setTimeout(measurePartSheet, 320);   // nakon animacije otvaranja
    return () => { ro.disconnect(); clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participantsOpen]);

  const openParticipants = () => {
    if (partTimerRef.current) { clearTimeout(partTimerRef.current); partTimerRef.current = null; }
    measurePartSheet();
    setParticipantsDragY(0);
    setParticipantsOpen(true);
    requestAnimationFrame(() => requestAnimationFrame(() => setParticipantsShown(true)));
  };
  const closeParticipants = () => {
    setParticipantsShown(false);
    setParticipantsDragging(false);
    if (partTimerRef.current) clearTimeout(partTimerRef.current);
    partTimerRef.current = setTimeout(() => {
      setParticipantsOpen(false);
      setParticipantsDragY(0);
      setPartView('main');
      setPartMenu(false);
      setApproveOpen(false);
      partTimerRef.current = null;
    }, 280);
  };
  // Drag zaglavlja sheeta sudionika prema dolje (pointer; zona ima touch-action: none) — isti obrazac kao FilterSheet
  const onPartHdrDown = (e) => {
    if (!isMobileTDM) return;
    partHdrRef.current = { y: e.clientY, active: true, cap: false, id: e.pointerId, el: e.currentTarget };
  };
  const onPartHdrMove = (e) => {
    const h = partHdrRef.current;
    if (!h.active) return;
    const dy = e.clientY - h.y;
    if (!h.cap) {
      if (dy <= 6) return;
      h.cap = true;
      setParticipantsDragging(true);
      try { h.el.setPointerCapture(h.id); } catch { /* ignore */ }
    }
    setParticipantsDragY(Math.max(0, dy));
  };
  const onPartHdrUp = (e) => {
    const h = partHdrRef.current;
    if (!h.active) return;
    const dy = e.clientY - h.y;
    h.active = false;
    if (h.cap) { try { h.el.releasePointerCapture(h.id); } catch { /* ignore */ } }
    setParticipantsDragging(false);
    if (dy > PARTICIPANTS_DISMISS) closeParticipants();
    else setParticipantsDragY(0);
  };

  // Overscroll-drag liste sudionika prema dolje (samo mobitel, samo kad je na vrhu) — isti obrazac kao FilterSheet
  useEffect(() => {
    if (!isMobileTDM || !participantsOpen) return;
    const el = partListRef.current;
    if (!el) return;
    let startY = 0;
    let active = false;
    const onStart = (e) => { startY = e.touches[0].clientY; active = false; };
    const onMove = (e) => {
      const dy = e.touches[0].clientY - startY;
      if (!active) {
        if (el.scrollTop <= 0 && dy > 4) { active = true; setParticipantsDragging(true); }
        else return;
      }
      e.preventDefault();
      setParticipantsDragY(Math.max(0, dy));
    };
    const onEnd = (e) => {
      if (!active) return;
      const dy = ((e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientY : startY)) - startY;
      active = false;
      setParticipantsDragging(false);
      if (dy > PARTICIPANTS_DISMISS) closeParticipants();
      else setParticipantsDragY(0);
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobileTDM, participantsOpen]);

  // Swipe okomito (gore/dolje): ako smo došli kroz modal (s glavnog na podzadatak) → natrag; inače (glavni
  //   zadatak ili direktno otvoren podzadatak) → zatvori. Swipe desno = natrag/na glavni; swipe lijevo = sljedeći
  //   podzadatak. (native listeneri zbog passive:false)
  useEffect(() => {
    const el = tdmModalRef.current;
    if (!el || !isMobileTDM) return;
    const inSubtask = () => navStack.length > 0 || !!task.parent_task_id;
    const siblingIdx = () => (task.parent_task_id ? siblingIds.indexOf(task.id) : -1);
    const canNextSub = () => siblingIds.length > 1 && siblingIdx() !== -1;
    // Swipe desno: natrag kroz navigaciju, ili na glavni zadatak (i kad je podzadatak otvoren direktno)
    const backNav = () => {
      if (navStack.length) goBack();
      else if (task.parent_task_id) switchToTask(task.parent_task_id, { push: false });
    };
    // Swipe okomito: natrag samo ako postoji povijest navigacije; direktno otvoren podzadatak → zatvori modal
    const verticalAction = () => {
      if (navStack.length) goBack();
      else onClose();
    };

    const reset = () => { navGRef.current = { x0: 0, y0: 0, axis: null, dir: null, tracking: false }; setNavDrag(d => (d.active || d.x || d.y) ? { x: 0, y: 0, active: false } : d); };

    const onStart = (e) => {
      if (e.touches.length !== 1 || participantsOpen || showClosePanel || switchingTask || showMoreMenu) {
        navGRef.current.tracking = false; return;
      }
      const t = e.touches[0];
      navGRef.current = { x0: t.clientX, y0: t.clientY, axis: null, dir: null, tracking: true };
    };
    const onMove = (e) => {
      const g = navGRef.current;
      if (!g.tracking) return;
      const t = e.touches[0];
      const dx = t.clientX - g.x0;
      const dy = t.clientY - g.y0;
      if (!g.axis) {
        if (Math.abs(dx) < NAV_LOCK && Math.abs(dy) < NAV_LOCK) return;
        if (Math.abs(dx) > Math.abs(dy)) {
          if (dx > 0 && inSubtask()) { g.axis = 'x'; g.dir = 'right'; }
          else if (dx < 0 && canNextSub()) { g.axis = 'x'; g.dir = 'left'; }
          else { g.tracking = false; return; }
        } else {
          const b = bodyRef.current;
          const atTop = !b || b.scrollTop <= 0;
          const atBottom = !b || (b.scrollHeight - b.scrollTop - b.clientHeight) <= 1;
          if (dy > 0 && atTop) { g.axis = 'y'; g.dir = 'down'; }
          else if (dy < 0 && atBottom) { g.axis = 'y'; g.dir = 'up'; }
          else { g.tracking = false; return; }
        }
      }
      e.preventDefault();
      if (g.axis === 'x') {
        const clamped = g.dir === 'right' ? Math.max(0, dx) : Math.min(0, dx);
        setNavDrag({ x: clamped * 0.7, y: 0, active: true });
      } else {
        const clamped = g.dir === 'down' ? Math.max(0, dy) : Math.min(0, dy);
        setNavDrag({ x: 0, y: clamped * 0.6, active: true });
      }
    };
    const onEnd = (e) => {
      const g = navGRef.current;
      const { axis, dir, tracking } = g;
      const ct = e.changedTouches && e.changedTouches[0];
      const dx = ct ? ct.clientX - g.x0 : 0;
      const dy = ct ? ct.clientY - g.y0 : 0;
      reset();
      if (!tracking || !axis) return;
      if (axis === 'y' && Math.abs(dy) > NAV_CLOSE_DY) verticalAction();
      else if (axis === 'x' && dir === 'right' && dx > NAV_BACK_DX) backNav();
      else if (axis === 'x' && dir === 'left' && -dx > NAV_BACK_DX && canNextSub()) {
        const next = siblingIds[(siblingIdx() + 1) % siblingIds.length];
        if (next && next !== task.id) switchToTask(next, { push: false });
      }
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobileTDM, participantsOpen, showClosePanel, switchingTask, showMoreMenu, navStack.length, task.parent_task_id, task.id, siblingIds]);

  const isGroupOwner = user.groups?.some(g => g.group_role === 'owner' && task.groups?.some(tg => tg.id === g.id));
  // Prijedlog (čeka ili odbijen): nema sudionika ni upravljanja — samo pregled i radnje prijedloga u footeru.
  const isProposal = task.status === 'proposed' || (task.status === 'rejected' && !!task.proposed_by);
  const isMyProposal = isProposal && task.proposed_by === user.email;
  const canManage = !isProposal && (user.role === 'admin' || isGroupOwner);

  const now2 = new Date();
  const taskDate2 = new Date(task.start_date);
  const endDate2 = task.end_date ? new Date(task.end_date) : null;
  const isPast2 = taskDate2 < now2;
  const isOverdue2 = isPast2 && task.status !== 'done' && endDate2 && endDate2 < now2;
  const isInProgress2 = isPast2 && task.status !== 'done' && (!endDate2 || endDate2 > now2);
  const startOfDay2 = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
  const startLockActive = task.start_locked === true && startOfDay2(taskDate2) > startOfDay2(now2);
  const startLockText = startLockActive
    ? `Zadatak možeš pokrenuti na dan početka — ${taskDate2.toLocaleDateString('hr-HR', { day: '2-digit', month: '2-digit', year: 'numeric' })}`
    : '';
  const assignments2 = task.assignments || [];
  const hasInProgress2 = assignments2.some(a => a.work_status === 'in_progress' || a.work_status === 'done');
  const isFailed2 = task.status === 'done' && task.close_result === 'failed';
  const isContainer = task.is_container === true;
  const containerSubs = isContainer ? (task.subtasks || []) : [];
  const containerDone = containerSubs.filter(s => s.status === 'done').length;
  const containerInProgress = isContainer && containerSubs.some(s => s.status === 'done' || s.work_status === 'in_progress' || s.work_status === 'done');
  const taskWorkStatus2 = task.status === 'done'
    ? (isFailed2 ? 'failed' : 'done')
    : isContainer
      ? (containerInProgress ? 'in_progress' : 'pending')
      : hasInProgress2 ? 'in_progress' : 'pending';
  const tdmKind = task.status === 'proposed' ? 'proposed'
    : task.status === 'rejected' ? 'failed'
    : isFailed2 ? 'failed'
    : isOverdue2 ? 'overdue'
    : taskWorkStatus2 === 'done' ? 'done'
    : (taskWorkStatus2 === 'in_progress' || isInProgress2) ? 'in_progress'
    : 'pending';
  const headerBorderColor = TDM_HUE[tdmKind];
  const failedMandatorySubtasks = (task.subtasks || []).filter(s => s.subtask_required && s.close_result === 'failed');

  const editor = useEditor({ extensions: [StarterKit], content: task.description || '', editable: false });

  // useEditor ne prati task.description reaktivno — bez ovoga bi skok na drugi zadatak
  // (switchToTask, roditelj↔podzadatak) i dalje prikazivao opis prethodno prikazanog zadatka.
  useEffect(() => {
    if (editor) editor.commands.setContent(task.description || '');
    if (partTimerRef.current) { clearTimeout(partTimerRef.current); partTimerRef.current = null; }
    setParticipantsOpen(false);
    setParticipantsShown(false);
    setParticipantsDragY(0);
    setConfirmRevert(false);
    setConfirmUnvolunteer(false);
    setDeclineFor(null);
    setDeclineReason('');
    setPartView('main');
    setPartMenu(false);
    setApproveOpen(false);
    setPreviewAttachment(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.id]);

  useEffect(() => {
    if (!showMoreMenu) return;
    const handler = (e) => {
      if (moreMenuRef.current && moreMenuRef.current.contains(e.target)) return;
      if (moreMenuPortalRef.current && moreMenuPortalRef.current.contains(e.target)) return;
      setShowMoreMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMoreMenu]);

  // Dok „Čekaju odobrenje”: otvoreni redak (potvrda odjave / odbijanje) cijeli u vidno polje doka
  useEffect(() => {
    if (!remDecide && !declineFor) return;
    const t = setTimeout(() => {
      document.querySelector('.tdm-approve-body .tdm-vrow.is-open')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 60);
    return () => clearTimeout(t);
  }, [remDecide, declineFor]);

  // Otvoreno iz obavijesti o prijavi / odjavi → odmah otvori panel sudionika (blok „Čekaju odobrenje” je na vrhu).
  useEffect(() => {
    if (!startParticipantsOpen || initialTask.is_container) return;
    const t = setTimeout(() => openParticipants(), 180);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Otvoreno preko "Detalji" (swipe kartica) → odskrolaj tijelo do te sekcije,
  // ne samo je razvij (kod duljeg opisa "Detalji" bi inače ostao ispod, van vidokruga).
  useEffect(() => {
    if (!startDetailsOpen) return;
    const t = setTimeout(() => {
      detailsSectionRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Meni "⋯" se renderira kroz portal (document.body) pa ga ne može odsjeći
  // overflow:hidden roditelja (modal) — pozicioniran fiksno po gumbu.
  useLayoutEffect(() => {
    if (!showMoreMenu || !moreBtnRef.current) return;
    const place = () => {
      const r = moreBtnRef.current.getBoundingClientRect();
      setMoreMenuPos({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [showMoreMenu]);

  useEffect(() => {
    fetchVolunteers();
    fetchRemovalRequests();
    api.get(`/tasks/${task.id}/attachments`).then(res => setAttachments(res.data)).catch(() => {});
    api.get(`/tasks/${task.id}/guests`).then(res => setTaskGuests(res.data)).catch(() => {});
  }, []);

  const refreshTask = async () => {
    try {
      const res = await api.get(`/tasks?year=${new Date(task.start_date).getFullYear()}`);
      const updated = res.data.find(t => t.id === task.id);
      if (updated) setTask(updated);
      onUpdate && onUpdate(false);
    } catch (err) {
      console.error(err);
      setError(err.response?.status === 429
        ? 'Previše zahtjeva u kratkom vremenu. Prikaz možda nije osvježen — pokušaj ponovno za par sekundi.'
        : 'Promjena je spremljena, ali osvježavanje prikaza nije uspjelo.');
    }
  };

  // Skok na povezani zadatak (roditelj/podzadatak) unutar istog modala. Ne oslanja se na
  // fetchVolunteers/fetchRemovalRequests (zatvaraju task.id iz starog rendera) nego dohvaća
  // sve povezane podatke eksplicitno za novi id.
  const switchToTask = async (id, { push = true } = {}) => {
    if (switchingTask) return;
    if (push && task?.id && task.id !== id) setNavStack(s => [...s, task.id]);
    setSwitchingTask(true);
    try {
      const [taskRes, volRes, remRes, attRes, guestRes] = await Promise.all([
        api.get(`/tasks/${id}`),
        api.get(`/tasks/${id}/volunteers`),
        api.get(`/tasks/${id}/removal-requests`),
        api.get(`/tasks/${id}/attachments`).catch(() => ({ data: [] })),
        api.get(`/tasks/${id}/guests`).catch(() => ({ data: [] })),
      ]);
      const newTask = taskRes.data;
      setTask(newTask);
      setVolunteers(volRes.data);
      setIsVolunteer(volRes.data.some(v => v.email === user.email && v.volunteer_status !== 'declined'));
      setRemovalRequests(remRes.data);
      setMyRemovalRequest(remRes.data.find(r => r.email === user.email && r.status === 'pending'));
      setAttachments(attRes.data);
      setTaskGuests(guestRes.data);
      setShowRemovalForm(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Zadatak nije pronađen.');
    } finally {
      setSwitchingTask(false);
    }
  };

  // Korak nazad kroz navStack (na zadatak s kojeg smo došli), bez guranja novog unosa.
  const goBack = () => {
    if (switchingTask || !navStack.length) return;
    const prevId = navStack[navStack.length - 1];
    setNavStack(s => s.slice(0, -1));
    switchToTask(prevId, { push: false });
  };


  const fetchVolunteers = async () => {
    try {
      const res = await api.get(`/tasks/${task.id}/volunteers`);
      setVolunteers(res.data);
      setIsVolunteer(res.data.some(v => v.email === user.email && v.volunteer_status !== 'declined'));
    } catch (err) { console.error(err); }
  };




  const fetchRemovalRequests = async () => {
    try {
      const res = await api.get(`/tasks/${task.id}/removal-requests`);
      setRemovalRequests(res.data);
      setMyRemovalRequest(res.data.find(r => r.email === user.email && r.status === 'pending'));
    } catch (err) { console.error(err); }
  };

  const handleVolunteer = async () => {
    try {
      if (isVolunteer) { await api.delete(`/tasks/${task.id}/volunteer`); }
      else { await api.post(`/tasks/${task.id}/volunteer`); }
      fetchVolunteers();
    } catch (err) { setError(err.response?.data?.error || 'Greška.'); }
  };


  // ── Zainteresirani: odobri / odbij (samo voditelj, samo pregled) ──
  const handleApproveVolunteer = async (email) => {
    if (volBusy) return;
    setVolBusy(email);
    try {
      await api.post(`/tasks/${task.id}/assignments`, { email, is_mandatory: false, action: 'add' });
      await refreshTask();
      await fetchVolunteers();
    } catch (err) { setError(err.response?.data?.error || 'Greška.'); }
    finally { setVolBusy(null); }
  };

  const handleApproveAll = async (emails) => {
    if (volBusy) return;
    setVolBusy('all');
    try {
      for (const email of emails) {
        await api.post(`/tasks/${task.id}/assignments`, { email, is_mandatory: false, action: 'add' });
      }
      await refreshTask();
      await fetchVolunteers();
    } catch (err) { setError(err.response?.data?.error || 'Greška.'); }
    finally { setVolBusy(null); }
  };

  const handleDeclineVolunteer = async (email) => {
    if (volBusy) return;
    setVolBusy(email);
    try {
      await api.put(`/tasks/${task.id}/volunteers/${encodeURIComponent(email)}`, { action: 'decline', reason: declineReason.trim() || undefined });
      setDeclineFor(null);
      setDeclineReason('');
      await fetchVolunteers();
      onUpdate && onUpdate(false);
    } catch (err) { setError(err.response?.data?.error || 'Greška.'); }
    finally { setVolBusy(null); }
  };

  const handleReopenVolunteer = async (email) => {
    if (volBusy) return;
    setVolBusy(email);
    try {
      await api.put(`/tasks/${task.id}/volunteers/${encodeURIComponent(email)}`, { action: 'reopen' });
      await fetchVolunteers();
      onUpdate && onUpdate(false);
    } catch (err) { setError(err.response?.data?.error || 'Greška.'); }
    finally { setVolBusy(null); }
  };



  const handleRespond = async (status) => {
    try { await api.put(`/tasks/${task.id}/respond`, { status }); await refreshTask(); }
    catch (err) { setError(err.response?.data?.error || 'Greška.'); }
  };

  // ── Akcije voditelja na sudioniku (panel sudionika, pregled) ──


  const handleWorkStatus = async (work_status) => {
    try {
      const res = await api.put(`/tasks/${task.id}/work-status`, { work_status });
      const { taskStatus } = res.data;
      // Zakrpa lokalno umjesto ponovnog GET /api/tasks — jedan klik inače lančano
      // zna okinuti i do 7 zahtjeva (modal refetch + parent fetchAll), što je gušilo nginx rate limit.
      setTask(prev => ({
        ...prev,
        status: taskStatus || prev.status,
        assignments: prev.assignments.map(a => a.email === user.email ? { ...a, work_status } : a),
      }));
      onUpdate && onUpdate(false, { taskId: task.id, email: user.email, work_status, taskStatus });
    }
    catch (err) { setError(err.response?.data?.error || 'Greška.'); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Obrisati zadatak?')) return;
    try { await api.delete(`/tasks/${task.id}`); onUpdate && onUpdate(true); onClose(); }
    catch (err) { setError(err.response?.data?.error || 'Greška pri brisanju.'); }
  };

  const rejectProposal = async () => {
    setPropBusy(true); setError('');
    try {
      await api.put(`/tasks/${task.id}/review`, { action: 'reject', reason: propReason.trim() });
      setPropReject(false); setPropReason('');
      await refreshTask();
    } catch (err) { setError(err.response?.data?.error || 'Odbijanje nije uspjelo.'); }
    setPropBusy(false);
  };
  const withdrawProposal = async () => {
    setPropBusy(true); setError('');
    try { await api.delete(`/tasks/${task.id}/proposal`); onUpdate && onUpdate(true); onClose(); }
    catch (err) { setError(err.response?.data?.error || 'Povlačenje nije uspjelo.'); setPropBusy(false); }
  };
  const fmtShort = (d) => new Date(d).toLocaleString('hr-HR', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' });

  const openClosePanel = () => { setCloseResult('success'); setCloseReason(''); setError(''); setShowClosePanel(true); };

  const handleClose = async () => {
    setClosing(true);
    try {
      await api.put(`/tasks/${task.id}/close`, { result: closeResult, reason: closeReason.trim() || undefined });
      setShowClosePanel(false);
      await refreshTask();
    }
    catch (err) { setError(err.response?.data?.error || 'Greška pri zatvaranju.'); }
    finally { setClosing(false); }
  };

  const handleReopen = async () => {
    try { await api.put(`/tasks/${task.id}/reopen`); await refreshTask(); }
    catch (err) { setError(err.response?.data?.error || 'Greška.'); }
  };

  const handleRemovalRequest = async () => {
    if (remBusy) return;
    setRemBusy('mine');
    try {
      await api.post(`/tasks/${task.id}/removal-request`, { message: removalMessage.trim() });
      setShowRemovalForm(false); setRemovalMessage(''); await fetchRemovalRequests();
    } catch (err) { setError(err.response?.data?.error || 'Greška pri slanju zahtjeva.'); }
    finally { setRemBusy(null); }
  };

  const handleWithdrawRemoval = async () => {
    if (remBusy) return;
    setRemBusy('mine');
    try { await api.delete(`/tasks/${task.id}/removal-request`); await fetchRemovalRequests(); }
    catch (err) { setError(err.response?.data?.error || 'Greška pri povlačenju zahtjeva.'); fetchRemovalRequests(); }
    finally { setRemBusy(null); }
  };

  const handleReviewRemoval = async (requestId, action) => {
    if (remBusy) return;
    setRemBusy(requestId);
    try {
      await api.put(`/tasks/removal-requests/${requestId}`, { action, reason: action === 'reject' ? remReason.trim() : undefined });
      setRemDecide(null); setRemReason('');
      await fetchRemovalRequests(); await refreshTask();
    }
    catch (err) { setError(err.response?.data?.error || 'Greška.'); setRemDecide(null); fetchRemovalRequests(); }
    finally { setRemBusy(null); }
  };

  const dismissRemovalNote = (id) => {
    const next = [...remSeen, id].slice(-50);
    setRemSeen(next);
    try { localStorage.setItem('tdm_rr_seen', JSON.stringify(next)); } catch { /* bez spremanja */ }
  };


  const sortedSubtasks = [...(task.subtasks || [])].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
  const myAssignment = task.assignments?.find(a => a.email === user.email);
  const acceptedAssignments = (task.assignments || []).filter(a => a.status === 'accepted').sort((a, b) => new Date(a.responded_at) - new Date(b.responded_at));
  const pendingAssignments = (task.assignments || []).filter(a => a.status === 'pending');
  const declinedAssignments = (task.assignments || []).filter(a => a.status === 'declined');
  const approvedRemovalAssignments = removalRequests.filter(r => r.status === 'approved');
  const pendingRemovals = removalRequests.filter(r => r.status === 'pending');
  const pendingRemovalEmails = new Set(pendingRemovals.map(r => r.email));
  const myLatestRemoval = removalRequests.find(r => r.email === user.email) || null;   // backend vraća najnoviji po osobi
  const myRejectedRemoval = myLatestRemoval?.status === 'rejected' && !remSeen.includes(myLatestRemoval.id) ? myLatestRemoval : null;
  const myApprovedRemoval = myLatestRemoval?.status === 'approved' ? myLatestRemoval : null;
  const assignedCount = (task.assignments?.filter(a => taskCountMode === 'accepted' ? a.status === 'accepted' : a.status !== 'declined').length || 0) + (taskGuests?.length || 0);
  const progressPct = Math.min(100, Math.round((assignedCount / (task.required_people || 1)) * 100));
  const unassignedVolunteers = volunteers.filter(v => !v.is_assigned && v.volunteer_status !== 'declined');
  const declinedVolunteers = volunteers.filter(v => !v.is_assigned && v.volunteer_status === 'declined');
  const myVolunteerDecline = declinedVolunteers.find(v => v.email === user.email) || null;
  const missingPeople = Math.max(0, (task.required_people || 1) - assignedCount);
  const canEditPeople = canManage && task.status !== 'done';
  const outCount = declinedAssignments.length + approvedRemovalAssignments.length + declinedVolunteers.length;
  // Panel sudionika: „čeka se odgovor” (voditelju bez javljenih — oni su u bloku „Čekaju odobrenje”) i „nisu na zadatku” (samo voditelj)
  const waitVolunteers = canEditPeople ? [] : unassignedVolunteers;
  const waitCount = pendingAssignments.length + waitVolunteers.length;
  const outN = canEditPeople ? outCount : 0;
  // Pogled iz ⋯ koji se ispraznio (npr. „Poništi” zadnje odbijene prijave) vraća se na sudionike
  const pView = (partView === 'wait' && waitCount === 0) || (partView === 'out' && outN === 0) ? 'main' : partView;





  const showRespondPanel = !isContainer && myAssignment && myAssignment.status === 'pending' && task.status !== 'done';
  const showWorkStatusPanel = !isContainer && myAssignment && myAssignment.status === 'accepted' && task.status !== 'done';

  // ── Jedan tok (samo pregled) — bez tabova, bez zaključane visine: sve ide u prirodnom scrollu ──
  const _d0 = new Date(now2); _d0.setHours(0, 0, 0, 0);
  const _d1 = new Date(taskDate2); _d1.setHours(0, 0, 0, 0);
  const tdmDiffDays = Math.round((_d1 - _d0) / 86400000);
  const hasSubtasks = !task.parent_task_id && (task.subtasks?.length > 0);
  const descEmpty = !task.description || task.description === '<p></p>';
  const subtasksFirst = hasSubtasks;   // zadatak s podzadacima: popis ide prije opisa

  const handleShareTask = async () => {
    const url = `${window.location.origin}/?task=${task.id}`;
    const tmp = document.createElement('div');
    tmp.innerHTML = task.description || '';
    const plain = (tmp.textContent || tmp.innerText || '').trim().slice(0, 250);
    const text = `*${task.title} (${assignedCount}/${task.required_people || 1})*${plain ? '\n' + plain : ''}`;
    try {
      if (navigator.share) { await navigator.share({ title: task.title, text, url }); return; }
      await navigator.clipboard.writeText(`${text}\n${url}`);
      setShareCopied(true); setTimeout(() => setShareCopied(false), 2000);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      try { await navigator.clipboard.writeText(`${text}\n${url}`); } catch {
        const el = document.createElement('textarea');
        el.value = `${text}\n${url}`;
        document.body.appendChild(el); el.select(); document.execCommand('copy'); document.body.removeChild(el);
      }
      setShareCopied(true); setTimeout(() => setShareCopied(false), 2000);
    }
  };

  return (
    <div className="modal-overlay tdm-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div
        ref={tdmModalRef}
        className="modal tdm-modal"
        style={{
          opacity: switchingTask ? 0.5 : (navDrag.active ? Math.max(0.45, 1 - Math.max(Math.abs(navDrag.x), Math.abs(navDrag.y)) / 340) : 1),
          transform: `translate(${navDrag.x}px, ${navDrag.y}px)`,
          transition: navDrag.active ? 'none' : 'opacity 0.15s, transform 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >

        {/* ── HEADER ── */}
          <div className="tdm-header tdm-header--view">
            {isMobileTDM && <div className="tdm-grab" aria-hidden="true" />}
            {/* Oznaka glavnog zadatka — iznad naslova (samo podzadatak, pregled) */}
            {task.parent_task_id && (
              <button
                type="button"
                className="tdm-parent-bar"
                onClick={() => navStack.length ? goBack() : switchToTask(task.parent_task_id, { push: false })}
                disabled={switchingTask}
                title="Otvori glavni zadatak"
              >
                <ArrowLeft size={15} className="tdm-parent-bar-ico" />
                <span className="tdm-parent-bar-lbl">Glavni zadatak</span>
                <span className="tdm-parent-bar-t">{task.parent_task_title}</span>
              </button>
            )}
            {/* Red naslova: naslov lijevo, ⋯ (i X na desktopu) u svom kutku skroz desno */}
            <div className="tdm-title-row">
              <h2 className="tdm-title">{task.title}</h2>
              <div className="tdm-title-actions" ref={moreMenuRef}>
                {(!isProposal || user.role === 'admin') && (
                  <button ref={moreBtnRef} className="tdm-close-x" onClick={() => setShowMoreMenu(v => !v)} title="Više">
                    <MoreHorizontal size={19} />
                  </button>
                )}
                {!isMobileTDM && (
                  <button className="tdm-close-x" onClick={onClose} title="Zatvori">
                    <X size={18} />
                  </button>
                )}

                {showMoreMenu && createPortal(
                  <div ref={moreMenuPortalRef} className="tdm-more-menu" style={{ position: 'fixed', top: moreMenuPos.top, right: moreMenuPos.right }}>
                    {!isProposal && (
                      <button className="tdm-more-menu-item" onClick={() => { setShowMoreMenu(false); handleShareTask(); }}>
                        {shareCopied ? <CheckCircle size={15} color="#3f8f6b" /> : <Share2 size={15} />}
                        {shareCopied ? 'Kopirano!' : 'Dijeli zadatak'}
                      </button>
                    )}
                    {!isProposal && (
                      <a href={`/api/tasks/${task.id}/ics`} download className="tdm-more-menu-item" onClick={() => setShowMoreMenu(false)}>
                        <CalendarPlus size={15} /> Dodaj u kalendar
                      </a>
                    )}

                    {canManage && <div className="tdm-more-menu-sep" />}
                    {canManage && (
                      <button className="tdm-more-menu-item" onClick={() => { setShowMoreMenu(false); setParticipantsOpen(false); setParticipantsShown(false); setFormOpen(true); }}>
                        <Pencil size={15} /> Uredi zadatak
                      </button>
                    )}
                    {canManage && (task.groups?.length > 0 || task.assignments?.some(a => a.status !== 'declined') || user.role === 'admin') && (
                      <button className="tdm-more-menu-item" onClick={() => { setShowMoreMenu(false); setNotifyOpen(true); }}>
                        <Bell size={15} /> Pošalji obavijest
                      </button>
                    )}

                    {canManage && <div className="tdm-more-menu-sep" />}
                    {canManage && task.status !== 'done' && (
                      <button className="tdm-more-menu-item" onClick={() => { setShowMoreMenu(false); openClosePanel(); }}>
                        <CheckCircle size={15} /> Zatvori zadatak
                      </button>
                    )}
                    {canManage && task.status === 'done' && (
                      <button className="tdm-more-menu-item" onClick={() => { setShowMoreMenu(false); handleReopen(); }}>
                        <RotateCcw size={15} /> Ponovno otvori
                      </button>
                    )}
                    {user.role === 'admin' && (
                      <button className="tdm-more-menu-item tdm-more-menu-danger" onClick={() => { setShowMoreMenu(false); handleDelete(); }}>
                        <Trash2 size={15} /> {isProposal ? 'Obriši prijedlog' : 'Obriši zadatak'}
                      </button>
                    )}
                  </div>,
                  document.body
                )}
              </div>
            </div>

            <div className="tdm-meta" style={{ alignItems: 'center' }}>
              <span className="tdm-meta-item"><Calendar size={14} />{formatDate(task.start_date)}</span>
              {!isPast2 && tdmDiffDays >= 0 && (
                <span className="tdm-meta-item" style={{ color: tdmDiffDays <= 1 ? '#ac463b' : '#8a94a6' }}>
                  {tdmDiffDays === 0 ? 'danas' : tdmDiffDays === 1 ? 'sutra' : `za ${tdmDiffDays} dana`}
                </span>
              )}
            </div>
            {isProposal && (
              <div className={`tdm-prop-by${task.status === 'rejected' ? ' is-rejected' : ''}`}>
                <Clock size={13} /> {task.status === 'rejected' ? 'Odbijen prijedlog' : 'Prijedlog'}
                {' · '}{isMyProposal ? 'tvoj' : (task.proposed_by_name || task.proposed_by)}
                {task.created_at ? ` · ${fmtShort(task.created_at)}` : ''}
              </div>
            )}
            {isFailed2 && task.close_reason && (
              <div style={{ marginTop: 8, fontSize: 13, color: '#8a2b2b', background: '#fde8e8', border: '1px solid #f5c6c6', borderRadius: 8, padding: '8px 10px', lineHeight: 1.45 }}>
                <strong>Razlog:</strong> {task.close_reason}
              </div>
            )}
          </div>

        {/* ── SCROLLABLE BODY — jedan tok, bez tabova ── */}
        <div className="tdm-body" ref={bodyRef}>

          {/* Podzadaci — status + napredak (glavni zadatak/container nema vlastite sudionike) */}
          {isContainer && (
            <div className="tdm-participants" style={{ cursor: 'default' }}>
              <div className="tdm-participants-top">
                <StatusStamp kind={tdmKind} />
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5, fontWeight: 600, color: '#1a2130' }}>
                  <ListTree size={15} color="#5b6472" /> Podzadaci
                </span>
                {containerSubs.length > 0
                  ? <span className="tdm-participants-count">{containerDone} / {containerSubs.length}</span>
                  : <span style={{ marginLeft: 'auto', fontSize: 13, color: '#9aa1ac' }}>nema podzadataka</span>}
              </div>
              {containerSubs.length > 0 && (
                <div className="tdm-progress" style={{ marginBottom: 0 }}>
                  <div className="tdm-progress-bar" style={{ width: `${Math.round((containerDone / containerSubs.length) * 100)}%`, background: headerBorderColor }} />
                </div>
              )}
            </div>
          )}

          {/* OPIS + PODZADACI — kod zadatka s podzadacima popis ide prvi (CSS order) */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>

          {/* OPIS */}
          {!(subtasksFirst && descEmpty) && (
          <div style={{ marginBottom: 24, order: subtasksFirst ? 2 : 1 }}>
            {(!task.description || task.description === '<p></p>') ? (
              <div style={{ fontSize: 15, color: '#b2bec3', fontStyle: 'italic' }}>Nema opisa.</div>
            ) : (
              <div className="tdm-description-flow">
                <EditorContent editor={editor} />
              </div>
            )}
          </div>
          )}

          {/* Podzadaci */}
          {!task.parent_task_id && task.subtasks?.length > 0 && (
            <div className="form-group" style={{ order: subtasksFirst ? 1 : 2 }}>
              {task.subtasks?.length > 0 && (
                <div>
                  {sortedSubtasks.map(st => (
                    <div key={st.id} className="tdm-person-row" style={{ cursor: 'pointer' }} onClick={() => { setSiblingIds(sortedSubtasks.map(s => s.id)); switchToTask(st.id); }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <CornerDownRight size={14} color="#b2bec3" />
                        <span className="tdm-person-name">{st.title}</span>
                        {st.mine_assigned
                          ? <span title="Sudjeluješ na ovom podzadatku" style={{ display: 'inline-flex' }}><UserCheck size={13} color="#00b894" /></span>
                          : st.mine_volunteer
                            ? <span title="Prijavljen/a za ovaj podzadatak" style={{ display: 'inline-flex' }}><Hand size={13} color="#0984e3" /></span>
                            : null}
                      </div>
                      <WorkStatusPill status={st.close_result === 'failed' ? 'failed' : (st.work_status || (st.status === 'done' ? 'done' : 'pending'))} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          </div>
          {/* /OPIS + PODZADACI */}

          {/* Prilozi */}
          {attachments.length > 0 && (
            <div className="form-group">
              {attachments.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {attachments.map(a => (
                    <div key={a.id} className="tdm-attachment-flow" style={{ cursor: 'pointer' }} onClick={() => setPreviewAttachment(a)}>
                      <div className="tdm-attachment-flow-ico"><Paperclip size={15} /></div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <button type="button" onClick={() => setPreviewAttachment(a)} title={a.original_name} className="tdm-attachment-flow-name" style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', font: 'inherit', cursor: 'pointer' }}>{a.original_name}</button>
                        <div className="tdm-attachment-flow-size">{formatFileSize(a.file_size)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* DETALJI — skupljeno po defaultu (prijedlog ih još nema) */}
          {!isProposal && (
            <div className="form-group" ref={detailsSectionRef} style={{ scrollMarginTop: 8 }}>
              <button onClick={detailsCollapse.toggle} className="tdm-collapse-btn tdm-collapse-btn--bare">
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>Detalji</span>
                {detailsCollapse.open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {detailsCollapse.open && (
              <div className="tdm-kv-card" style={{ marginTop: 8, animation: detailsCollapse.visible ? 'slideDown 0.22s cubic-bezier(0.4, 0, 0.2, 1)' : 'collapseUp 0.22s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <dl className="tdm-kv">
                  {task.categories?.length > 0 && <><dt>Kategorija</dt><dd>{task.categories.map(c => `${c.emoji ? c.emoji + ' ' : ''}${c.name}`).join(', ')}</dd></>}
                  {task.groups?.length > 0 && <><dt>{task.groups.length > 1 ? 'Grupe' : 'Grupa'}</dt><dd>{task.groups.map(g => g.name).join(', ')}</dd></>}
                  <dt>Vidljivost</dt><dd>{task.is_public ? 'Javno' : 'Privatno'}</dd>
                  {task.is_recurring && <><dt>Ponavljanje</dt><dd>Dan {task.recur_day}</dd></>}
                  {task.parent_task_title && <><dt>Glavni zadatak</dt><dd>{task.parent_task_title}</dd></>}
                  {isContainer && <><dt>Povezani zadaci</dt><dd>{containerDone}/{containerSubs.length}</dd></>}
                  <dt>Početak</dt><dd>{formatDate(task.start_date)}</dd>
                  {task.end_date && <><dt>Završetak</dt><dd>{formatDate(task.end_date)}</dd></>}
                  {task.closed_by && <><dt>Zatvorio</dt><dd>{task.closed_by}</dd></>}
                </dl>
              </div>
              )}
            </div>
          )}


          {error && <p className="error">{error}</p>}
        </div>

        {/* ── PANEL SUDIONIKA — raste prema gore iz trake sudionika; traka i footer ostaju vidljivi ── */}
        {!isContainer && participantsOpen && (
          <div
            className="tdm-participants-scrim"
            onClick={closeParticipants}
            style={{ bottom: partSheetPos.bottom, opacity: participantsShown ? 1 : 0 }}
          />
        )}
        {!isContainer && participantsOpen && (
          <div
            className="tdm-participants-sheet"
            style={{
              bottom: partSheetPos.bottom,
              maxHeight: partSheetPos.maxH || '58vh',
              transform: participantsDragging
                ? `translateY(${participantsDragY}px)`
                : (participantsShown ? 'translateY(0)' : 'translateY(100%)'),
              transition: participantsDragging ? 'none' : 'transform 0.26s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <div
              className="tdm-participants-sheet-hdr"
              style={{ touchAction: isMobileTDM ? 'none' : 'auto' }}
              onPointerDown={onPartHdrDown}
              onPointerMove={onPartHdrMove}
              onPointerUp={onPartHdrUp}
              onPointerCancel={onPartHdrUp}
            >
              <div className="tdm-participants-grab" />
                <div className="tdm-participants-sheet-top">
                  {pView === 'main' ? (
                    <>
                      <span style={{ fontSize: 17, fontWeight: 800, color: '#1a2130' }}>Sudionici</span>
                      <span style={{ fontSize: 14.5, fontWeight: 600, color: '#5b6472' }}>{assignedCount} / {task.required_people || 1}</span>
                    </>
                  ) : (
                    <>
                      <button type="button" className="tdm-pback" onClick={() => setPartView('main')} title="Natrag na sudionike">
                        <ArrowLeft size={19} />
                      </button>
                      <span style={{ fontSize: 17, fontWeight: 800, color: pView === 'out' ? '#ac463b' : '#1a2130' }}>
                        {pView === 'out' ? 'Nisu na zadatku' : 'Čeka se odgovor'}
                      </span>
                      <span style={{ fontSize: 14.5, fontWeight: 600, color: '#5b6472' }}>{pView === 'out' ? outN : waitCount}</span>
                    </>
                  )}
                  {pView === 'main' && (waitCount + outN) > 0 && (
                    <span className="tdm-pmenu-wrap" style={{ marginLeft: 'auto' }}>
                      <button type="button" className="tdm-close-x" onClick={() => setPartMenu(o => !o)} title="Više">
                        <MoreHorizontal size={19} />
                      </button>
                      {partMenu && (
                        <>
                          <div className="tdm-pmenu-scrim" onClick={() => setPartMenu(false)} />
                          <div className="tdm-pmenu">
                            {waitCount > 0 && (
                              <button type="button" onClick={() => { setPartView('wait'); setPartMenu(false); }}>
                                <Clock size={17} />
                                <span style={{ flex: 1 }}>Čeka se odgovor</span>
                                <span className="tdm-pmenu-n">{waitCount}</span>
                              </button>
                            )}
                            {outN > 0 && (
                              <button type="button" onClick={() => { setPartView('out'); setPartMenu(false); }}>
                                <X size={17} />
                                <span style={{ flex: 1 }}>Nisu na zadatku</span>
                                <span className="tdm-pmenu-n">{outN}</span>
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </span>
                  )}
                  <button className="tdm-close-x" style={{ marginLeft: (pView === 'main' && (waitCount + outN) > 0) ? 0 : 'auto' }} onClick={closeParticipants} title="Zatvori">
                    <X size={18} />
                  </button>
                </div>
            </div>
            <div className="tdm-participants-list tdm-pview" ref={partListRef}>
              {pView === 'main' && (
                <>
                {(acceptedAssignments.length + taskGuests.length + waitCount + outN) === 0 && (
                  <div style={{ padding: '18px 4px', textAlign: 'center', fontSize: 14, color: '#8a919c' }}>Još nitko nije dodijeljen.</div>
                )}
                {(acceptedAssignments.length > 0 || taskGuests.length > 0) && (
                  <div className="tdm-plist">
                    {acceptedAssignments.map((a, i) => (
                      <div key={a.email} className="tdm-prow">
                        <span className="tdm-prow-num">{i + 1}.</span>
                        <Avatar name={a.full_name || a.email} size={36} />
                        <span className="tdm-prow-name">{a.full_name || a.email}{a.email === user.email && <span className="tdm-prow-you"> · ti</span>}</span>
                        {a.is_mandatory && <AlertTriangle size={14} color="#ac463b" strokeWidth={2.4} style={{ flexShrink: 0 }} />}
                        {pendingRemovalEmails.has(a.email) && (canManage || a.email === user.email)
                          ? <span className="tdm-prow-tag tdm-prow-tag-rem"><LogOut size={11} strokeWidth={2.6} /> traži odjavu</span>
                          : <span className="tdm-prow-ws" data-ws={a.work_status || 'pending'}>{wsLabel(a.work_status)}</span>}
                      </div>
                    ))}
                    {taskGuests.map((g, i) => (
                      <div key={g.id} className="tdm-prow">
                        <span className="tdm-prow-num">{acceptedAssignments.length + i + 1}.</span>
                        <Avatar name={g.full_name} size={36} color="#b2bec3" />
                        <span className="tdm-prow-name">{g.full_name}</span>
                        <span className="tdm-prow-tag">Gost</span>
                      </div>
                    ))}
                  </div>
                )}
                </>
              )}
              {pView === 'wait' && (
                <div className="tdm-plist">
                  {waitVolunteers.map(v => (
                    <div key={v.email} className="tdm-prow is-faded">
                      <Avatar name={v.full_name || v.email} size={36} />
                      <span className="tdm-prow-name">{v.full_name || v.email}{v.email === user.email && <span className="tdm-prow-you"> · ti</span>}</span>
                      <span className="tdm-prow-tag" style={{ color: '#3a6ea3', borderColor: '#c9d8ea' }}>javio/la se</span>
                    </div>
                  ))}
                  {pendingAssignments.map(a => (
                    <div key={a.email} className="tdm-prow is-faded">
                      <Avatar name={a.full_name || a.email} size={36} />
                      <span className="tdm-prow-name">{a.full_name || a.email}{a.email === user.email && <span className="tdm-prow-you"> · ti</span>}</span>
                      {a.is_mandatory && <AlertTriangle size={14} color="#ac463b" strokeWidth={2.4} style={{ flexShrink: 0 }} />}
                      <span className="tdm-prow-tag">poziv</span>
                    </div>
                  ))}
                </div>
              )}
              {pView === 'out' && (
                <div className="tdm-plist">
                  {declinedAssignments.map(a => (
                    <div key={a.email} className="tdm-prow is-faded">
                      <Avatar name={a.full_name || a.email} size={36} color="#c9ccd2" />
                      <span className="tdm-prow-name">{a.full_name || a.email}</span>
                      <span className="tdm-prow-tag" style={{ color: '#ac463b', borderColor: '#e2b7b1' }}>Odbio poziv</span>
                    </div>
                  ))}
                  {approvedRemovalAssignments.map(r => (
                    <div key={r.email} className="tdm-prow is-faded">
                      <Avatar name={r.full_name || r.email} size={36} color="#d8c39a" />
                      <span className="tdm-prow-name">{r.full_name || r.email}</span>
                      <span className="tdm-prow-tag" style={{ color: '#a8781f', borderColor: '#e3d3b0' }}>Odsutan/na</span>
                    </div>
                  ))}
                  {declinedVolunteers.map(v => (
                    <div key={v.email} className="tdm-prow is-faded" style={{ opacity: volBusy === v.email ? 0.55 : 1 }}>
                      <Avatar name={v.full_name || v.email} size={36} color="#c9ccd2" />
                      <span className="tdm-prow-name">
                        {v.full_name || v.email}
                        {v.reason && <span className="tdm-prow-sub">„{v.reason}”</span>}
                      </span>
                      <span className="tdm-prow-tag" style={{ color: '#ac463b', borderColor: '#e2b7b1' }}>Prijava odbijena</span>
                      <button type="button" className="tdm-link-btn" disabled={!!volBusy} onClick={() => handleReopenVolunteer(v.email)}>Poništi</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Voditelj: javljeni — pribijeno na dno panela, iznad trake sudionika; širi se prema gore */}
            {pView === 'main' && canEditPeople && (unassignedVolunteers.length + pendingRemovals.length) > 0 && (
              <div className={`tdm-approve tdm-approve-dock${approveOpen ? ' is-open' : ''}`}>
                <button type="button" className="tdm-approve-hd" onClick={() => setApproveOpen(o => !o)}>
                  <span className="tdm-approve-dot" />
                  <span className="tdm-approve-t">
                    Čekaju odobrenje<span className="c"> · {unassignedVolunteers.length + pendingRemovals.length}</span>
                    <span className="tdm-approve-sub">
                      {[
                        pendingRemovals.length > 0 && `${pendingRemovals.length} ${pendingRemovals.length === 1 ? 'odjava' : 'odjave'}`,
                        pendingRemovals.length > 0 && unassignedVolunteers.length > 0 && `${unassignedVolunteers.length} ${unassignedVolunteers.length === 1 ? 'prijava' : 'prijave'}`,
                        missingPeople > 0 ? `fali još ${missingPeople}` : 'zadatak je popunjen',
                      ].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  {!approveOpen && (
                    <span className="tdm-av-stack">
                      {[...pendingRemovals, ...unassignedVolunteers].slice(0, 3).map(v => <Avatar key={v.email} name={v.full_name || v.email} size={24} />)}
                    </span>
                  )}
                  {approveOpen ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                </button>
                {approveOpen && (
                  <div className="tdm-approve-body">
                    {pendingRemovals.length > 0 && unassignedVolunteers.length > 0 && (
                      <div className="tdm-approve-grp"><LogOut size={12} strokeWidth={2.6} /> Odjave</div>
                    )}
                    {pendingRemovals.map(r => {
                      const mode = remDecide?.id === r.id ? remDecide.mode : null;
                      const busy = remBusy === r.id;
                      const name = r.full_name || r.email;
                      const when = new Date(r.created_at).toLocaleString('hr-HR', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' });
                      const after = Math.max(0, assignedCount - 1);
                      const missAfter = Math.max(0, (task.required_people || 1) - after);
                      return (
                        <div key={r.id} className={`tdm-vrow${mode ? ' is-open' : ''}`} style={{ opacity: busy ? 0.55 : 1 }}>
                          <div className="tdm-prow">
                            <Avatar name={name} size={36} />
                            <span className="tdm-prow-name">
                              {name}
                              {r.message && <span className="tdm-prow-sub tdm-rem-msg">„{r.message}”</span>}
                              <span className="tdm-prow-sub">traži odjavu · {when}</span>
                            </span>
                            {!mode && (
                              <>
                                <button type="button" className="tdm-ib tdm-ib-no" title="Odbij odjavu" disabled={!!remBusy} onClick={() => { setRemDecide({ id: r.id, mode: 'no' }); setRemReason(''); }}>
                                  <X size={17} />
                                </button>
                                <button type="button" className="tdm-ib tdm-ib-ok" title="Odobri odjavu" disabled={!!remBusy} onClick={() => setRemDecide({ id: r.id, mode: 'ok' })}>
                                  <Check size={17} />
                                </button>
                              </>
                            )}
                          </div>
                          {mode === 'ok' && (
                            <div className="tdm-decline">
                              <div className="tdm-decline-q" style={{ color: '#1a2130' }}>Odjaviti sa zadatka?</div>
                              <div className="tdm-rem-conseq">
                                Nakon toga je {after} / {task.required_people || 1}{missAfter > 0 ? `, fali još ${missAfter}` : ''}. {name.split(' ')[0]} dobiva obavijest.
                              </div>
                              <div className="tdm-decline-btns">
                                <button type="button" className="tdm-btn-flow tdm-btn-flow-primary" style={{ flex: 'none', padding: '0 16px' }} disabled={!!remBusy} onClick={() => handleReviewRemoval(r.id, 'approve')}><LogOut size={15} /> Odjavi</button>
                                <button type="button" className="tdm-btn-flow tdm-btn-flow-outline" style={{ flex: 'none', padding: '0 16px' }} onClick={() => setRemDecide(null)}>Odustani</button>
                              </div>
                            </div>
                          )}
                          {mode === 'no' && (
                            <div className="tdm-decline">
                              <div className="tdm-decline-q">Odbiti odjavu?</div>
                              <div className="tdm-decline-chips">
                                {['Nema zamjene', 'Obavezno sudjelovanje', 'Javi se ranije'].map(x => (
                                  <button type="button" key={x} className={remReason === x ? 'on' : ''} onClick={() => setRemReason(x)}>{x}</button>
                                ))}
                              </div>
                              <textarea
                                value={remReason}
                                onChange={e => setRemReason(e.target.value)}
                                maxLength={500}
                                rows={2}
                                placeholder={`Razlog (neobavezno) — vidi ga samo ${name.split(' ')[0]}`}
                              />
                              <div className="tdm-decline-btns">
                                <button type="button" className="tdm-btn-flow tdm-btn-flow-red" style={{ flex: 'none', padding: '0 16px' }} disabled={!!remBusy} onClick={() => handleReviewRemoval(r.id, 'reject')}>Odbij odjavu</button>
                                <button type="button" className="tdm-btn-flow tdm-btn-flow-outline" style={{ flex: 'none', padding: '0 16px' }} onClick={() => { setRemDecide(null); setRemReason(''); }}>Odustani</button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {pendingRemovals.length > 0 && unassignedVolunteers.length > 0 && (
                      <div className="tdm-approve-grp"><Hand size={12} strokeWidth={2.6} /> Prijave</div>
                    )}
                    {unassignedVolunteers.length > 1 && unassignedVolunteers.length <= missingPeople && (
                      <button type="button" className="tdm-approve-all-text" disabled={!!volBusy} onClick={() => handleApproveAll(unassignedVolunteers.map(v => v.email))}>
                        Prihvati sve
                      </button>
                    )}
                    {unassignedVolunteers.map(v => {
                      const open = declineFor === v.email;
                      const busy = volBusy === v.email || volBusy === 'all';
                      const when = new Date(v.created_at).toLocaleDateString('hr-HR', { day: 'numeric', month: 'numeric' });
                      return (
                        <div key={v.email} className={`tdm-vrow${open ? ' is-open' : ''}`} style={{ opacity: busy ? 0.55 : 1 }}>
                          <div className="tdm-prow">
                            <Avatar name={v.full_name || v.email} size={36} />
                            <span className="tdm-prow-name">
                              {v.full_name || v.email}
                              <span className="tdm-prow-sub">javio/la se {when}</span>
                            </span>
                            {!open && (
                              <>
                                <button type="button" className="tdm-ib tdm-ib-no" title="Odbij prijavu" disabled={!!volBusy} onClick={() => { setDeclineFor(v.email); setDeclineReason(''); }}>
                                  <X size={17} />
                                </button>
                                <button type="button" className="tdm-ib tdm-ib-ok" title="Prihvati" disabled={!!volBusy} onClick={() => handleApproveVolunteer(v.email)}>
                                  <Check size={17} />
                                </button>
                              </>
                            )}
                          </div>
                          {open && (
                            <div className="tdm-decline">
                              <div className="tdm-decline-q">Odbiti prijavu?</div>
                              <div className="tdm-decline-chips">
                                {['Popunjeno', 'Traži se iskustvo', 'Termin se promijenio'].map(r => (
                                  <button type="button" key={r} className={declineReason === r ? 'on' : ''} onClick={() => setDeclineReason(r)}>{r}</button>
                                ))}
                              </div>
                              <textarea
                                value={declineReason}
                                onChange={e => setDeclineReason(e.target.value)}
                                maxLength={500}
                                rows={2}
                                placeholder={`Razlog (neobavezno) — vidi ga samo ${(v.full_name || v.email).split(' ')[0]}`}
                              />
                              <div className="tdm-decline-btns">
                                <button type="button" className="tdm-btn-flow tdm-btn-flow-red" style={{ flex: 'none', padding: '0 16px' }} disabled={!!volBusy} onClick={() => handleDeclineVolunteer(v.email)}>Odbij prijavu</button>
                                <button type="button" className="tdm-btn-flow tdm-btn-flow-outline" style={{ flex: 'none', padding: '0 16px' }} onClick={() => { setDeclineFor(null); setDeclineReason(''); }}>Odustani</button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

          </div>
        )}


        {/* ── SUDIONICI — pribijeno na vrh footera; status + broj + progres; klik toggle panela ── */}
        {!isContainer && !showClosePanel && !isProposal && (
          <div className="tdm-footer-roster" ref={rosterBarRef}>
            <button type="button" className="tdm-participants" onClick={() => (participantsOpen ? closeParticipants() : openParticipants())}>
              <div className="tdm-participants-top">
                <StatusStamp kind={tdmKind} />
                <span className="tdm-participants-count">{assignedCount} / {task.required_people || 1}</span>
                {canManage && unassignedVolunteers.length > 0 && (
                  <span className="tdm-roster-flag" title={`${unassignedVolunteers.length} zainteresiran${unassignedVolunteers.length === 1 ? '/a' : 'ih'} čeka raspoređivanje`}>
                    <span className="tdm-roster-flag-dot" />+{unassignedVolunteers.length}
                  </span>
                )}
                {canManage && pendingRemovals.length > 0 && (
                  <span className="tdm-roster-flag" title={`${pendingRemovals.length} ${pendingRemovals.length === 1 ? 'zahtjev' : 'zahtjeva'} za odjavu`}>
                    <LogOut size={12} strokeWidth={2.6} />{pendingRemovals.length}
                  </span>
                )}
                {participantsOpen
                  ? <ChevronDown size={15} color="#c7ccd4" style={{ marginLeft: 'auto', flexShrink: 0 }} />
                  : <ChevronUp size={15} color="#c7ccd4" style={{ marginLeft: 'auto', flexShrink: 0 }} />}
              </div>
              <div className="tdm-progress" style={{ marginBottom: 0 }}>
                <div className="tdm-progress-bar" style={{ width: `${progressPct}%`, background: progressPct >= 100 ? (isOverdue2 ? '#d63031' : '#00b894') : headerBorderColor }} />
              </div>
            </button>
          </div>
        )}

        {/* ── STICKY FOOTER — jedna kontekstualna akcija, bez utility reda (Uredi/obavijest/ICS su u ⋯ izborniku) ── */}
        {showClosePanel ? (
          <div className="tdm-footer">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1a2a4a' }}>Zatvaranje zadatka</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setCloseResult('success')} style={{ flex: 1, padding: '8px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, border: `2px solid ${closeResult === 'success' ? '#3f8f6b' : '#dfe6e9'}`, background: closeResult === 'success' ? '#e7f5ef' : 'white', color: closeResult === 'success' ? '#3f8f6b' : '#636e72' }}>✓ Uspješno odrađeno</button>
                <button onClick={() => setCloseResult('failed')} style={{ flex: 1, padding: '8px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, border: `2px solid ${closeResult === 'failed' ? '#ac463b' : '#dfe6e9'}`, background: closeResult === 'failed' ? '#fbe0dd' : 'white', color: closeResult === 'failed' ? '#ac463b' : '#636e72' }}>✕ Nije odrađeno</button>
              </div>
              {closeResult === 'failed' && (
                <textarea value={closeReason} onChange={e => setCloseReason(e.target.value)} maxLength={1000} placeholder="Razlog (opcionalno) — npr. vježba otkazana zbog kiše, bez novog termina" style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', border: '1px solid #dfe6e9', borderRadius: 8, fontSize: 14, resize: 'vertical', minHeight: 56 }} />
              )}
              {closeResult === 'success' && failedMandatorySubtasks.length > 0 && (
                <div style={{ fontSize: 13, color: '#8a5a00', background: '#fff4e0', border: '1px solid #ffe0b0', borderRadius: 8, padding: '8px 10px', lineHeight: 1.45 }}>
                  {failedMandatorySubtasks.length === 1 ? '1 obavezan podzadatak je neuspješan' : `${failedMandatorySubtasks.length} obaveznih podzadataka je neuspješno`} — svejedno zatvoriti kao uspješno?
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="tdm-btn-flow tdm-btn-flow-outline" onClick={() => setShowClosePanel(false)} disabled={closing}>Odustani</button>
                <button className={`tdm-btn-flow ${closeResult === 'failed' ? 'tdm-btn-flow-red' : 'tdm-btn-flow-green'}`} onClick={handleClose} disabled={closing}>
                  <CheckCircle size={15} /> {closing ? 'Zatvaram…' : 'Zatvori zadatak'}
                </button>
              </div>
            </div>
          </div>
        ) : isProposal ? (
          <div className="tdm-footer">
            {task.status === 'rejected' ? (
              <>
                <div className="tdm-declined-note">
                  <div className="tdm-declined-note-h"><X size={15} /> Prijedlog odbijen</div>
                  {task.reject_reason && <div className="tdm-declined-note-r">„{task.reject_reason}”</div>}
                  <div className="tdm-declined-note-m">
                    {task.reviewed_by_name ? `${task.reviewed_by_name} · ` : ''}{task.reviewed_at ? fmtShort(task.reviewed_at) : ''}
                  </div>
                </div>
                {isMyProposal && (
                  <div style={{ display: 'flex' }}>
                    <button className="tdm-btn-flow tdm-btn-flow-outline" disabled={propBusy} onClick={withdrawProposal}>
                      <Trash2 size={15} /> Makni s popisa
                    </button>
                  </div>
                )}
              </>
            ) : user.role === 'admin' ? (
              propReject ? (
                <div className="tdm-rem-form">
                  <div className="tdm-rem-form-h"><X size={17} /> Odbij prijedlog</div>
                  <div className="tdm-decline-chips tdm-chips-navy">
                    {['Već postoji takav zadatak', 'Termin ne odgovara', 'Nije za DVD'].map(x => (
                      <button type="button" key={x} className={propReason === x ? 'on' : ''} onClick={() => setPropReason(propReason === x ? '' : x)}>{x}</button>
                    ))}
                  </div>
                  <textarea value={propReason} onChange={e => setPropReason(e.target.value)} maxLength={500} rows={2} placeholder="Razlog (neobavezno)" />
                  <div className="tdm-rem-hint">Predlagatelj dobiva obavijest i vidi razlog.</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="tdm-btn-flow tdm-btn-flow-outline" disabled={propBusy} onClick={() => { setPropReject(false); setPropReason(''); }}>Odustani</button>
                    <button className="tdm-btn-flow tdm-btn-flow-red" disabled={propBusy} onClick={rejectProposal}>{propBusy ? 'Odbijam…' : 'Odbij prijedlog'}</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="tdm-btn-flow tdm-btn-flow-outline" onClick={() => setPropReject(true)}>Odbij</button>
                  <button className="tdm-btn-flow tdm-btn-flow-primary" onClick={() => setPropForm('approve')}>Pregledaj i objavi</button>
                </div>
              )
            ) : isMyProposal ? (
              propWithdraw ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: '#2d3436' }}>Povući prijedlog?</span>
                  <button onClick={withdrawProposal} disabled={propBusy} className="tdm-btn-flow tdm-btn-flow-red" style={{ flex: 'none', padding: '0 16px' }}>Povuci</button>
                  <button onClick={() => setPropWithdraw(false)} className="tdm-btn-flow tdm-btn-flow-outline" style={{ flex: 'none', padding: '0 16px' }}>Odustani</button>
                </div>
              ) : (
                <>
                  <div className="tdm-rem-note">
                    <div className="tdm-rem-note-h">
                      <Clock size={15} /> Čeka odobrenje admina
                      <button type="button" className="tdm-rem-note-act" onClick={() => setPropWithdraw(true)}>Povuci</button>
                    </div>
                    <div className="tdm-rem-note-m">Do odobrenja prijedlog vidiš samo ti.</div>
                  </div>
                  <div style={{ display: 'flex' }}>
                    <button className="tdm-btn-flow tdm-btn-flow-outline" onClick={() => setPropForm('proposal')}><Pencil size={15} /> Uredi prijedlog</button>
                  </div>
                </>
              )
            ) : null}
          </div>
        ) : isContainer ? null : showRespondPanel ? (
          <div className="tdm-footer">
            {myAssignment.is_mandatory && (
              <div className="tdm-footer-note"><AlertTriangle size={13} /> Sudjelovanje je obavezno.</div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              {!myAssignment.is_mandatory && <button className="tdm-btn-flow tdm-btn-flow-outline" onClick={() => handleRespond('declined')}>Odbij</button>}
              <button className="tdm-btn-flow tdm-btn-flow-primary" onClick={() => handleRespond('accepted')}>Prihvati</button>
            </div>
          </div>
        ) : showWorkStatusPanel ? (
          <div className="tdm-footer">
            {showRemovalForm ? (
              <div className="tdm-rem-form">
                <div className="tdm-rem-form-h"><LogOut size={17} /> Odjava sa zadatka</div>
                <div className="tdm-decline-chips tdm-chips-navy">
                  {['Bolestan/na', 'Poslovne obveze', 'Obiteljske obveze', 'Ne stižem na vrijeme'].map(x => (
                    <button type="button" key={x} className={removalMessage === x ? 'on' : ''} onClick={() => setRemovalMessage(removalMessage === x ? '' : x)}>{x}</button>
                  ))}
                </div>
                <textarea value={removalMessage} onChange={e => setRemovalMessage(e.target.value)} maxLength={500} rows={2} placeholder="Razlog (neobavezno)" />
                <div className="tdm-rem-hint">Voditelj mora odobriti odjavu. Do tada si i dalje na zadatku.</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="tdm-btn-flow tdm-btn-flow-outline" onClick={() => { setShowRemovalForm(false); setRemovalMessage(''); }}>Odustani</button>
                  <button className="tdm-btn-flow tdm-btn-flow-red" disabled={!!remBusy} onClick={handleRemovalRequest}>Pošalji zahtjev</button>
                </div>
              </div>
            ) : (
              <>
              {myRemovalRequest ? (
                <div className="tdm-rem-note">
                  <div className="tdm-rem-note-h">
                    <Clock size={15} /> Odjava čeka odobrenje
                    <button type="button" className="tdm-rem-note-act" disabled={!!remBusy} onClick={handleWithdrawRemoval}>Povuci</button>
                  </div>
                  <div className="tdm-rem-note-m">
                    {myRemovalRequest.message ? `„${myRemovalRequest.message}” · ` : ''}poslano {new Date(myRemovalRequest.created_at).toLocaleString('hr-HR', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              ) : myRejectedRemoval && (
                <div className="tdm-declined-note">
                  <div className="tdm-declined-note-h">
                    <X size={15} /> Odjava odbijena
                    <button type="button" className="tdm-rem-note-x" title="Zatvori" onClick={() => dismissRemovalNote(myRejectedRemoval.id)}><X size={15} /></button>
                  </div>
                  {myRejectedRemoval.reject_reason && <div className="tdm-declined-note-r">„{myRejectedRemoval.reject_reason}”</div>}
                  <div className="tdm-declined-note-m">
                    {myRejectedRemoval.reviewed_by_name ? `${myRejectedRemoval.reviewed_by_name} · ` : ''}
                    {myRejectedRemoval.reviewed_at ? new Date(myRejectedRemoval.reviewed_at).toLocaleString('hr-HR', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {/* Odjava sa zadatka — sakriveno kad je posao završen (nema se od čega odjaviti) i dok zahtjev čeka */}
                {myAssignment.work_status !== 'done' && !confirmRevert && !myRemovalRequest && (
                  <button onClick={() => setShowRemovalForm(true)} title="Zatraži odjavu sa zadatka" className="tdm-btn-flow-icon-only">
                    <LogOut size={17} />
                  </button>
                )}
                {myAssignment.work_status === 'pending' && (
                  startLockActive ? (
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, height: 44, borderRadius: 10, background: '#e7f5ef', color: '#3f8f6b', fontSize: 14, fontWeight: 700 }}>
                        <UserCheck size={15} /> Sudjeluješ
                      </span>
                      <span style={{ fontSize: 11.5, color: '#8a94a6', textAlign: 'center', lineHeight: 1.35 }}>{startLockText}</span>
                    </div>
                  ) : (
                    <button onClick={() => handleWorkStatus('in_progress')} className="tdm-btn-flow tdm-btn-flow-blue">
                      <Play size={15} fill="white" /> Započni
                    </button>
                  )
                )}
                {myAssignment.work_status === 'in_progress' && (
                  <>
                    <button onClick={() => handleWorkStatus('pending')} className="tdm-btn-flow tdm-btn-flow-outline">
                      <Pause size={15} /> Pauziraj
                    </button>
                    <button onClick={() => handleWorkStatus('done')} className="tdm-btn-flow tdm-btn-flow-green">
                      <CheckCircle size={15} /> Završeno
                    </button>
                  </>
                )}
                {myAssignment.work_status === 'done' && (
                  confirmRevert ? (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: '#2d3436' }}>Vratiti u tijeku?</span>
                      <button onClick={() => { setConfirmRevert(false); handleWorkStatus('in_progress'); }} className="tdm-btn-flow tdm-btn-flow-blue" style={{ flex: 'none', padding: '0 16px' }}>
                        <RotateCcw size={14} /> Vrati
                      </button>
                      <button onClick={() => setConfirmRevert(false)} className="tdm-btn-flow tdm-btn-flow-outline" style={{ flex: 'none', padding: '0 16px' }}>
                        Odustani
                      </button>
                    </div>
                  ) : (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, height: 44, borderRadius: 10, background: '#e7f5ef', color: '#3f8f6b', fontSize: 14, fontWeight: 700 }}>
                        <CheckCircle size={15} /> Završeno
                      </span>
                      <button onClick={() => setConfirmRevert(true)} title="Vrati zadatak u tijeku" style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 10px', border: 'none', background: 'none', color: '#8a94a6', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', transition: 'color 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.color = '#636e72'}
                        onMouseLeave={e => e.currentTarget.style.color = '#8a94a6'}>
                        <RotateCcw size={13} /> Vrati u tijeku
                      </button>
                    </div>
                  )
                )}
              </div>
              </>
            )}
          </div>
        ) : !myAssignment && myVolunteerDecline && task.status !== 'done' ? (
          <div className="tdm-footer">
            <div className="tdm-declined-note">
              <div className="tdm-declined-note-h"><X size={15} /> Prijava nije prihvaćena</div>
              {myVolunteerDecline.reason && <div className="tdm-declined-note-r">„{myVolunteerDecline.reason}”</div>}
              <div className="tdm-declined-note-m">
                {myVolunteerDecline.decided_by_name ? `${myVolunteerDecline.decided_by_name} · ` : ''}
                {myVolunteerDecline.decided_at ? new Date(myVolunteerDecline.decided_at).toLocaleDateString('hr-HR', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
              </div>
            </div>
          </div>
        ) : (!myAssignment || myAssignment.status === 'declined') && task.status !== 'done' ? (
          <div className="tdm-footer">
            {confirmUnvolunteer ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: '#2d3436' }}>Odjaviti se sa zadatka?</span>
                <button onClick={() => { setConfirmUnvolunteer(false); handleVolunteer(); }} className="tdm-btn-flow tdm-btn-flow-red" style={{ flex: 'none', padding: '0 16px' }}>
                  Odjavi se
                </button>
                <button onClick={() => setConfirmUnvolunteer(false)} className="tdm-btn-flow tdm-btn-flow-outline" style={{ flex: 'none', padding: '0 16px' }}>
                  Odustani
                </button>
              </div>
            ) : (
              <>
              {myApprovedRemoval && !isVolunteer && (
                <div className="tdm-rem-note is-out">
                  <div className="tdm-rem-note-h"><LogOut size={15} /> Odjavljen/a si sa zadatka</div>
                  <div className="tdm-rem-note-m">
                    {myApprovedRemoval.reviewed_by_name ? `${myApprovedRemoval.reviewed_by_name} je odobrio/la odjavu` : 'Odjava je odobrena'}
                    {myApprovedRemoval.reviewed_at ? ` ${new Date(myApprovedRemoval.reviewed_at).toLocaleString('hr-HR', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}.
                  </div>
                </div>
              )}
              <div style={{ display: 'flex' }}>
                <button className={`tdm-btn-flow ${isVolunteer ? 'tdm-btn-flow-red' : 'tdm-btn-flow-green'}`} style={{ whiteSpace: 'nowrap' }} onClick={() => { if (isVolunteer) setConfirmUnvolunteer(true); else handleVolunteer(); }}>
                  {isVolunteer ? 'Odjavi se' : myApprovedRemoval ? 'Javi se ponovno' : 'Javi se za sudjelovanje'}
                </button>
              </div>
              </>
            )}
          </div>
        ) : null}

      </div>


      {notifyOpen && (
        <TaskNotifySheet task={task} user={user} onClose={() => setNotifyOpen(false)}
          onSent={(n) => { setNotifyOpen(false); setToast(`Obavijest poslana: ${n} ${n === 1 ? 'osoba' : n < 5 ? 'osobe' : 'osoba'}`); setTimeout(() => setToast(''), 3500); }} />
      )}
      {toast && <div className="tdm-toast" role="status"><Check size={18} /> {toast}</div>}
      {formOpen && (
        <TaskForm
          user={user}
          task={task}
          onClose={() => setFormOpen(false)}
          onSaved={async () => { setFormOpen(false); await refreshTask(); api.get(`/tasks/${task.id}/attachments`).then(r => setAttachments(r.data)).catch(() => {}); }}
          onSubtasksChanged={refreshTask}
        />
      )}

      {propForm && (
        <TaskForm
          user={user}
          task={task}
          mode={propForm}
          onClose={() => setPropForm(null)}
          onSaved={async () => {
            const approved = propForm === 'approve';
            setPropForm(null);
            await refreshTask();
            if (approved) { setToast('Prijedlog odobren i objavljen'); setTimeout(() => setToast(''), 3500); }
          }}
        />
      )}

      {previewAttachment && <AttachmentViewer attachment={previewAttachment} onClose={() => setPreviewAttachment(null)} />}
    </div>
  );
}

export default TaskDetailModal;
